// ./src/screens/BracketHubScreen.js

import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, FlatList, StyleSheet, Pressable, Image, ActivityIndicator, TextInput, TouchableOpacity } from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { db } from '../../firebaseConfig';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';

export default function BracketHubScreen() {
  const { params } = useRoute();
  const navigation = useNavigation();
  const eventId = params?.eventId;

  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [streamers, setStreamers] = useState([]);

  // Filters
  const [nameQuery, setNameQuery] = useState('');
  const [filterKey, setFilterKey] = useState('All'); // All | Rookie | Top finishes | Record

  // Needs Host tracking (Phase 5)
  const [unhostMatches, setUnhostMatches] = useState([]);
  const [claimingMatchId, setClaimingMatchId] = useState(null);
  const currentUid = getAuth().currentUser?.uid;

  useEffect(() => {
    (async () => {
      try {
        // Get event — live events are 'tournaments' docs ('events' is the
        // abandoned legacy collection this screen was written against, which
        // left it rendering nothing for every real event).
        const eSnap = await getDoc(doc(db, 'tournaments', eventId));
        if (!eSnap.exists()) { setLoading(false); return; }
        const e = { id: eSnap.id, ...eSnap.data() };
        setEvent(e);

        // Load participants (entries subcollection or your participants array)
        let participants = [];
        try {
          const entriesSnap = await getDocs(collection(db, 'tournaments', eventId, 'entries'));
          entriesSnap.forEach(d => participants.push({ id: d.id, ...d.data() }));
        } catch (_) {
          // fallback if you store participants on the event
          if (Array.isArray(e?.participants)) participants = e.participants;
        }

        // Normalize to streamer cards (only those with a stream profile)
        const rows = participants
          .map(p => {
            const u = p?.user || p; // in case entry contains embedded user
            const stream = u?.stream || p?.stream;
            if (!stream?.provider) return null;

            const wins = Number(u?.stats?.wins || p?.wins || 0);
            const losses = Number(u?.stats?.losses || p?.losses || 0);
            const earnings = Number(u?.stats?.winningsDollars || p?.winningsDollars || 0);
            const rookie = !!(u?.isRookie || p?.isRookie);

            // Thumbnail best-effort
            let thumb = null;
            if (stream.provider === 'youtube' && stream.videoId) {
              thumb = `https://img.youtube.com/vi/${stream.videoId}/hqdefault.jpg`;
            } else if (u?.avatarUrl) {
              thumb = u.avatarUrl;
            }

            return {
              id: u?.uid || p?.userId || p?.id,
              name: u?.displayName || p?.displayName || 'Player',
              stream,
              wins, losses, earnings, rookie,
              thumb,
            };
          })
          .filter(Boolean);

        setStreamers(rows);

        // Load matches that need hosts (Phase 5). Live matches are seeded
        // into the 'matches' subcollection — 'bracketMatches' was never
        // written by anything, so the claim cards were permanently empty
        // (same H5b bug the claimUnhostedMatch lookup already fixed).
        try {
          const matchesSnap = await getDocs(collection(db, 'tournaments', eventId, 'matches'));
          const unhosted = [];
          matchesSnap.forEach(d => {
            const m = { id: d.id, ...d.data() };
            if (m.needsHost === true && m.status !== 'completed' && m.status !== 'cancelled') {
              unhosted.push(m);
            }
          });
          setUnhostMatches(unhosted);
        } catch (_) {
          // Non-fatal — bracket may not exist yet
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId]);

  const filtered = useMemo(() => {
    let rows = streamers;

    if (nameQuery.trim()) {
      const q = nameQuery.trim().toLowerCase();
      rows = rows.filter(r => r.name.toLowerCase().includes(q));
    }

    switch (filterKey) {
      case 'Rookie':
        rows = rows.filter(r => r.rookie);
        break;
      case 'Top finishes':
        rows = [...rows].sort((a, b) => (b.earnings - a.earnings));
        break;
      case 'Record':
        rows = [...rows].sort((a, b) => {
          const awp = a.wins + a.losses > 0 ? a.wins / (a.wins + a.losses) : 0;
          const bwp = b.wins + b.losses > 0 ? b.wins / (b.wins + b.losses) : 0;
          return bwp - awp;
        });
        break;
      default:
        break;
    }

    return rows;
  }, [streamers, nameQuery, filterKey]);

  // Claim an unhosted match (Phase 5)
  const [claimError, setClaimError] = useState(null);
  const claimHostSlot = async (matchId) => {
    if (claimingMatchId) return;
    setClaimingMatchId(matchId);
    setClaimError(null);
    try {
      const functions = getFunctions(undefined, 'us-central1');
      await httpsCallable(functions, 'claimUnhostedMatch')({
        eventId,
        matchId,
        hostTosAccepted: true,
      });
      // Remove from the unhosted list
      setUnhostMatches(prev => prev.filter(m => m.id !== matchId));
    } catch (err) {
      // Surface the backend's rejection (participant block, TOS, already
      // hosted…) — a silent console.warn read as a dead button.
      const msg = String(err?.message || 'Try again.').replace(/^.*?:\s*/, '');
      setClaimError(msg);
      console.warn('[BracketHub] Claim failed:', err.message);
    } finally {
      setClaimingMatchId(null);
    }
  };

  const goStream = (r) => {
    // You can either embed here or push to your existing LiveMatchScreen with override
    navigation.push('LiveMatch', {
      streamOverride: r.stream,
      ownerUid: r.id,
      eventId,
    });
  };

  const RenderRow = ({ item }) => (
    <Pressable onPress={() => goStream(item)} style={styles.row}>
      <View style={styles.thumbWrap}>
        {item.thumb ? (
          <Image source={{ uri: item.thumb }} style={styles.thumb} />
        ) : (
          <View style={[styles.thumb, styles.thumbFallback]}><Text style={{ color: '#999' }}>LIVE</Text></View>
        )}
      </View>
      <View style={{ flex: 1 }}>
        <Text style={styles.name} numberOfLines={1}>{item.name}</Text>
        <Text style={styles.meta} numberOfLines={1}>
          {item.wins}-{item.losses} career
          {item.rookie ? ' • Rookie' : ''}
        </Text>
      </View>
      <View style={styles.liveBadge}><Text style={styles.liveTxt}>LIVE</Text></View>
    </Pressable>
  );

  if (loading) return <View style={styles.center}><ActivityIndicator /></View>;

  return (
    <View style={styles.screen}>
      <View style={styles.header}>
        <Text style={styles.title}>{event?.name || event?.eventName || event?.title || 'Bracket Hub'}</Text>
        <Text style={styles.subtitle}>Browse live streamers in this bracket</Text>
        <View style={styles.searchRow}>
          <TextInput
            placeholder="Filter by name..."
            placeholderTextColor="#7a7a80"
            style={styles.input}
            value={nameQuery}
            onChangeText={setNameQuery}
          />
        </View>
        <View style={styles.filters}>
          {['All', 'Rookie', 'Top finishes', 'Record'].map(k => (
            <Pressable key={k} onPress={() => setFilterKey(k)} style={[styles.chip, filterKey === k && styles.chipActive]}>
              <Text style={[styles.chipText, filterKey === k && styles.chipTextActive]}>{k}</Text>
            </Pressable>
          ))}
        </View>
      </View>

      {/* Needs Host Banner (Phase 5) */}
      {unhostMatches.length > 0 && currentUid && (
        <View style={styles.needsHostBanner}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
            <Ionicons name="shield-checkmark" size={16} color="#FCD000" style={{ marginRight: 6 }} />
            <Text style={{ color: '#FCD000', fontSize: 13, fontWeight: '700' }}>
              {unhostMatches.length} match{unhostMatches.length !== 1 ? 'es' : ''} need a host
            </Text>
          </View>
          {unhostMatches.slice(0, 3).map(m => (
            <View key={m.id} style={styles.needsHostRow}>
              <Text style={{ color: '#ccc', fontSize: 12, flex: 1 }}>
                Round {m.roundNumber || m.round || '?'} - {m.matchType === 'heat' ? 'Heat' : 'Match'} {m.matchNumber || m.id.slice(-4)}
              </Text>
              <TouchableOpacity
                onPress={() => claimHostSlot(m.id)}
                disabled={!!claimingMatchId}
                style={[styles.claimBtn, claimingMatchId === m.id && { opacity: 0.5 }]}
              >
                <Text style={styles.claimBtnText}>
                  {claimingMatchId === m.id ? 'Claiming...' : 'Claim'}
                </Text>
              </TouchableOpacity>
            </View>
          ))}
          {unhostMatches.length > 3 && (
            <Text style={{ color: '#888', fontSize: 11, marginTop: 4 }}>
              +{unhostMatches.length - 3} more
            </Text>
          )}
          {claimError && (
            <Text style={{ color: '#ff6b6b', fontSize: 12, marginTop: 6 }}>
              Could not claim: {claimError}
            </Text>
          )}
        </View>
      )}

      <FlatList
        data={filtered}
        keyExtractor={(i) => String(i.id)}
        renderItem={RenderRow}
        ItemSeparatorComponent={() => <View style={{ height: 10 }} />}
        contentContainerStyle={{ padding: 12 }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#0b0b0d' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  header: { paddingHorizontal: 12, paddingTop: 12, paddingBottom: 8, borderBottomWidth: StyleSheet.hairlineWidth, borderColor: '#222' },
  title: { color: '#fff', fontWeight: '800', fontSize: 18 },
  subtitle: { color: '#9aa0a6', marginTop: 2 },
  searchRow: { marginTop: 8 },
  input: { backgroundColor: '#141417', color: '#fff', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, borderWidth: 1, borderColor: '#24242a' },
  filters: { flexDirection: 'row', gap: 8, marginTop: 10, flexWrap: 'wrap' },
  chip: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 999, backgroundColor: '#16161a', borderWidth: 1, borderColor: '#2a2a30' },
  chipActive: { backgroundColor: '#ffe100', borderColor: '#ffe100' },
  chipText: { color: '#cfcfd3', fontWeight: '600' },
  chipTextActive: { color: '#0b0b0d' },

  row: { flexDirection: 'row', gap: 10, alignItems: 'center', padding: 10, backgroundColor: '#121215', borderRadius: 12, borderWidth: 1, borderColor: '#1d1d22' },
  thumbWrap: { width: 84, height: 48, borderRadius: 8, overflow: 'hidden', backgroundColor: '#0f0f12' },
  thumb: { width: '100%', height: '100%' },
  thumbFallback: { alignItems: 'center', justifyContent: 'center' },
  name: { color: '#fff', fontSize: 16, fontWeight: '700' },
  meta: { color: '#9aa0a6', marginTop: 2 },
  liveBadge: { backgroundColor: '#e11900', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6 },
  liveTxt: { color: '#fff', fontWeight: '800', fontSize: 12, letterSpacing: 0.5 },

  // Needs Host styles (Phase 5)
  needsHostBanner: {
    backgroundColor: '#1a1700',
    borderWidth: 1,
    borderColor: '#FCD000',
    borderRadius: 10,
    padding: 12,
    marginHorizontal: 12,
    marginTop: 8,
  },
  needsHostRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  claimBtn: {
    backgroundColor: '#FCD000',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 5,
  },
  claimBtnText: {
    color: '#000',
    fontSize: 11,
    fontWeight: '800',
  },
});
